/**
 * Referral Promotion Templates
 * Drive organic growth through referral incentives
 */

import { PromotionTemplate } from "./types";

export const REFERRAL_TEMPLATES: PromotionTemplate[] = [
  {
    id: "double-sided-referral",
    name: "Double-Sided Referral",
    category: "referral",
    description: "Classic referral program where both referrer and referee get rewards",
    strategy: "Double-sided referrals are the gold standard (Dropbox, Uber). Both parties benefit, creating strong motivation to share. The referred user gets a discount, making conversion easier.",
    icon: "UsergroupAddOutlined",
    color: "#10B981",
    expectedOutcomes: [
      "Viral growth loop",
      "Lower CAC than paid acquisition",
      "Higher quality leads (friend recommendations)",
    ],
    bestPractices: [
      "Make rewards valuable for both sides",
      "Keep sharing frictionless",
      "Track and display referral progress",
      "Celebrate successful referrals",
    ],
    tags: ["referral", "viral", "growth", "double-sided"],
    bestFor: ["Consumer apps", "B2B with team features", "Community products"],
    difficulty: "intermediate",
    setupTime: "20 min",
    defaults: {
      type: "banner",
      displayStyle: "standard",
      priority: 5,
      targetAudience: ["paid"],
      discount: {
        discountType: "percentage",
        discountValue: 25,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "default",
        primaryColor: "#10B981",
        secondaryColor: "#3B82F6",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 3,
        delaySeconds: 10,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Give $10, Get $10",
          subheadline: "Share Copilotly with friends. You both get $10 credit!",
          ctaText: "Invite Friends",
          ctaSecondaryText: "Learn More",
          badge: "REFER",
          terms: "*Credit applied when referred user subscribes.",
        },
      },
    },
  },
  {
    id: "referral-contest",
    name: "Referral Contest",
    category: "referral",
    description: "Competitive referral campaign with leaderboard and prizes",
    strategy: "Gamify referrals with a time-limited contest. Top referrers win big prizes. Creates competition and social sharing. Best run monthly or quarterly.",
    icon: "TrophyOutlined",
    color: "#F59E0B",
    expectedOutcomes: [
      "Burst of referral activity",
      "Top user engagement",
      "Social media buzz",
    ],
    bestPractices: [
      "Offer desirable prizes",
      "Show real-time leaderboard",
      "Celebrate winners publicly",
      "Keep contest period short (2-4 weeks)",
    ],
    tags: ["referral", "contest", "gamification", "competitive"],
    bestFor: ["Products with engaged communities", "Social products"],
    difficulty: "advanced",
    setupTime: "30 min",
    defaults: {
      type: "popup",
      displayStyle: "bold",
      priority: 7,
      targetAudience: ["paid", "free"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 0,
        currency: "USD",
        applicablePlans: [],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#F59E0B",
        secondaryColor: "#EF4444",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 5,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Referral Challenge!",
          subheadline: "Top referrer wins 1 year FREE. Second place gets 6 months. Game on!",
          ctaText: "Start Referring",
          ctaSecondaryText: "See Leaderboard",
          badge: "CONTEST",
          terms: "*Contest ends in 14 days. Top 3 referrers win.",
        },
      },
    },
  },
  {
    id: "team-referral",
    name: "Team Referral Bonus",
    category: "referral",
    description: "Encourage teams to grow by referring colleagues",
    strategy: "Team plans grow through internal referrals. Make it easy for team admins to invite colleagues with incentives for growing the team.",
    icon: "TeamOutlined",
    color: "#6366F1",
    expectedOutcomes: [
      "Team plan expansion",
      "Higher LTV through team growth",
      "Organic B2B growth",
    ],
    bestPractices: [
      "Target team admins specifically",
      "Offer per-seat discounts",
      "Make team management easy",
      "Highlight collaboration benefits",
    ],
    tags: ["referral", "team", "b2b", "expansion"],
    bestFor: ["B2B SaaS", "Collaboration tools", "Team productivity apps"],
    difficulty: "intermediate",
    setupTime: "20 min",
    defaults: {
      type: "banner",
      displayStyle: "standard",
      priority: 6,
      targetAudience: ["paid"],
      discount: {
        discountType: "percentage",
        discountValue: 20,
        currency: "USD",
        applicablePlans: ["enterprise"],
      },
      styling: {
        theme: "default",
        primaryColor: "#6366F1",
        secondaryColor: "#8B5CF6",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard", "billing"],
        maxImpressionsPerUser: 3,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Grow Your Team",
          subheadline: "Add 3+ team members and save 20% on your entire plan.",
          ctaText: "Invite Team",
          ctaSecondaryText: "Learn About Teams",
          badge: "TEAM BONUS",
          terms: "*Discount applies when team reaches 5+ members.",
        },
      },
    },
  },
];
